<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  ButtonComponent,
  MyLoadsMobileComingSoonBannerComponent,
  MyLoadsCardComponent,
  TeleportModalWrapperComponent,
  MyLoadsDesktopFiltersComponent,
  CarrierWorkflowModalComponent,
  MyLoadsDropdownSelectComponent,
  MyLoadsDesktopWelcomeBannerComponent,
  displayEquipmentTableModal,
  MyLoadsPostCapacityBannerComponent,
  EquipmentChartSectionComponent,
  MyLoadsSectionComponent,
  RecommendedLoads,
} from '@/components';
import { useScreenSize } from '@/composables/useScreenSize';

const { screenSize, innerWidth } = useScreenSize();
const isTablet = computed(() => screenSize.value === 'tablet');
const isMobile = computed(() => screenSize.value === 'mobile');
const isDesktop = computed(() => screenSize.value === 'desktop');

const isWebView =
  typeof window !== 'undefined' &&
  typeof window.ReactNativeWebView !== 'undefined';

const { pageTabs } = defineProps({
  pageTabs: {
    type: Object,
    required: true,
    default: () => ({}),
  },
});

const pageTabsDropdownList = computed(() =>
  Object.keys(pageTabs).map((tab) => ({
    id: tab,
    label: pageTabs[tab].title,
  }))
);
const router = useRouter();
const store = useStore();

const showAllLoads = computed(() => false);

const emit = defineEmits(['setActivePageTab']);

const showFilters = ref(false);

const name = computed(() => router.currentRoute.value.name);
const canViewMyLoads = computed(() => {
  return (
    store.getters['users/userIsADriver'] ||
    store.state.users.currentUser?.scopes?.includes('carrier_load_abilities')
  );
});

const canViewCarrierRecommendations = computed(() =>
  store.state.users.currentUser?.scopes?.includes('carrier_view_posted_loads')
);

const isShowEquipmentChartEnabled = computed(
  () => store.state.users.featureFlags?.fe_carrier_show_equipment_chart?.enabled
);

const userIsADriver = computed(() => store.getters['users/userIsADriver']);

const activePageTab = computed(() => {
  if (name.value === 'My Loads') {
    const queryTab = router.currentRoute.value.query.tab;
    if (queryTab) {
      return queryTab;
    }
    // Default tab: 'in-transit' for driver users, 'unassigned' for others
    return userIsADriver.value ? 'in-transit' : 'unassigned';
  }
  return userIsADriver.value ? 'in-transit' : 'unassigned';
});

const unassignedLoads = computed(() =>
  store.state.myLoadsStore.unassigned.loads.length > 4
    ? store.state.myLoadsStore.unassigned.loads.slice(0, 4)
    : store.state.myLoadsStore.unassigned.loads
);
const unassignedLoadsCount = computed(
  () => store.state.myLoadsStore.unassigned.pagination.totalRecords
);
const loads = computed(() =>
  name.value === 'My Loads'
    ? store.state.myLoadsStore[activePageTab.value].loads
    : unassignedLoads.value
);

const isLoading = computed(
  () => store.state.myLoadsStore[activePageTab.value].isLoading
);
const pagination = computed(
  () => store.state.myLoadsStore[activePageTab.value].pagination
);

const showAssignVehicleModal = ref(false);
const selectedLoad = ref({});
const selectedLoadOrigin = ref({});
const selectedLoadDestination = ref({});
const selectedLoadAssignedVehicle = computed(
  () =>
    selectedLoad.value.carrier_vehicle || {
      id: '',
      truck_number: '',
      vin: '',
    }
);
const selectedLoadPendingVehicle = computed(
  () =>
    selectedLoad.value.pending_driver_carrier_vehicle || {
      id: '',
      truck_number: '',
      vin: '',
    }
);

const selectedLoadTicketId = computed(
  () => selectedLoad.value.tickets?.[0]?.id || ''
);

const carrierVisibleTicketId = computed(
  () => selectedLoad.value.carrier_visible_tickets?.[0]?.id || ''
);

const showEquipmentModal = ref(false);

const filtersCount = computed(
  () => store.state.myLoadsStore[activePageTab.value].activeFiltersCount
);

const handlePageClick = (page) => {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  router.push({
    query: {
      ...router.currentRoute.value.query,
      page: page,
    },
  });
};

store.dispatch('loads/getLaneSearchHistory');
</script>

<template>
  <div>
    <MyLoadsDesktopWelcomeBannerComponent
      v-if="name !== 'My Loads' && !isMobile"
    />
    <div
      v-if="canViewMyLoads"
      class="relative flex h-screen w-full flex-col items-center overflow-auto bg-steel-100 pb-6"
      :class="name === 'My Loads' ? 'pb-14' : ''"
    >
      <div class="container-max w-full">
        <div
          v-if="name !== 'My Loads' && isMobile"
          class="mt-4 flex flex-col gap-2"
          :class="{
            'mx-6': canViewCarrierRecommendations,
            'mx-4': !canViewCarrierRecommendations,
          }"
        >
          <MyLoadsMobileComingSoonBannerComponent
            container-class="w-full h-[150px]"
          />
        </div>

        <div
          v-if="name !== 'My Loads'"
          class="flex w-full items-start justify-start gap-4 pt-4"
        >
          <RecommendedLoads
            v-if="canViewCarrierRecommendations"
          ></RecommendedLoads>
        </div>

        <div
          v-if="isMobile && name !== 'My Loads'"
          class="mt-4 flex w-full flex-wrap items-center justify-center gap-4 px-4"
          :class="!isMobile ? 'max-w-[400px]' : ''"
        >
          <MyLoadsPostCapacityBannerComponent
            container-class="w-full h-[52px]"
          />

          <EquipmentChartSectionComponent v-if="isShowEquipmentChartEnabled" />
        </div>
      </div>

      <div
        v-if="name !== 'My Loads' && !isMobile"
        class="container-max flex w-full justify-center gap-2 overflow-hidden px-6 py-6"
      >
        <div
          v-if="isShowEquipmentChartEnabled"
          class="w-fit"
        >
          <EquipmentChartSectionComponent />
        </div>
        <div class="min-w-0 max-w-full flex-1">
          <MyLoadsSectionComponent
            @show-assign-vehicle-modal="
              (load, origin, destination) => {
                showAssignVehicleModal = true;
                selectedLoad = load;
                selectedLoadOrigin = origin;
                selectedLoadDestination = destination;
              }
            "
          />
        </div>
      </div>
      <div
        class="mt-4 w-full px-4"
        v-if="isMobile && name !== 'My Loads'"
      >
        <MyLoadsSectionComponent
          @show-assign-vehicle-modal="
            (load, origin, destination) => {
              showAssignVehicleModal = true;
              selectedLoad = load;
              selectedLoadOrigin = origin;
              selectedLoadDestination = destination;
            }
          "
        />
      </div>
      <div
        v-if="isMobile && name === 'My Loads'"
        class="container-max sticky top-0 z-10 w-full"
      >
        <div
          class="flex w-full items-center justify-between bg-steel-100 px-4 py-4"
        >
          <div class="flex w-full flex-col items-start justify-center">
            <div class="flex w-full items-center justify-between">
              <div
                v-if="!isWebView"
                class="justify-start font-medium leading-loose text-steel-900"
                :class="innerWidth < 300 ? 'text-base' : 'text-xl'"
              >
                My Loads
              </div>
            </div>
            <div
              v-if="name !== 'My Loads'"
              class="justify-center text-xs font-normal leading-none tracking-tight text-steel-700"
            >
              {{
                loads.length > 1
                  ? `1 - ${loads.length} of ${unassignedLoadsCount} Action Needed Loads`
                  : `${loads.length === 1 ? '1 Load' : '0 Loads'}`
              }}
            </div>
            <div
              v-else
              class="flex w-full items-center justify-between gap-2"
            >
              <MyLoadsDropdownSelectComponent
                :selected-item-id="activePageTab"
                :dropdown-list="pageTabsDropdownList"
                button-class="min-w-[180px] flex items-center justify-start gap-2"
                selected-class="text-sun-400"
                @select-item="(_, item) => emit('setActivePageTab', item.id)"
              />
              <div class="flex items-center gap-2">
                <p
                  class="text-xs font-normal leading-none tracking-tight text-steel-700"
                >
                  {{ `${filtersCount} Filter${filtersCount !== 1 ? 's' : ''}` }}
                </p>
                <ButtonComponent
                  variant="transparent"
                  class="!h-8 border border-steel-300 bg-steel-200 !px-2 !py-0"
                  @click="showFilters = true"
                >
                  <img
                    class="h-3 w-3"
                    src="@/assets/filter.svg"
                    alt="filter"
                  />
                </ButtonComponent>
              </div>
            </div>
          </div>
          <ButtonComponent
            v-if="name !== 'My Loads'"
            variant="transparent"
            class="border border-steel-300"
            @click="router.push({ name: 'My Loads' })"
          >
            View All
          </ButtonComponent>
        </div>
      </div>
      <div
        v-if="name === 'My Loads'"
        class="container-max fixed bottom-0 z-10 w-full"
      >
        <div
          class="flex w-full items-center justify-center bg-steel-100 py-4"
          :class="!isMobile ? 'px-20' : isTablet ? 'px-10' : 'px-4'"
        >
          <div
            class="flex items-center justify-center gap-2 text-xs font-normal leading-none tracking-tight text-steel-700"
          >
            <ButtonComponent
              :disabled="pagination.currentPage === 1"
              click="handlePageClick(pagination.currentPage - 1)"
              aria-label="Previous page"
            >
              <img
                class="h-4 w-4 rotate-180"
                src="@/assets/chevron-right.svg"
              />
            </ButtonComponent>
            <div>
              {{
                loads.length > 0
                  ? `${(pagination.currentPage - 1) * 50 + 1}`
                  : 0
              }}
              - {{ (pagination.currentPage - 1) * 50 + loads.length + '' }}
            </div>
            <div>
              {{
                pagination.totalPages > 1
                  ? `of ${pagination.totalRecords} Loads`
                  : `Loads`
              }}
            </div>
            <ButtonComponent
              :disabled="pagination.currentPage === pagination.totalPages"
              @click="handlePageClick(pagination.currentPage + 1)"
              aria-label="Next page"
            >
              <img
                class="h-4 w-4"
                src="@/assets/chevron-right.svg"
              />
            </ButtonComponent>
          </div>
        </div>
      </div>
      <div
        v-if="isMobile && isLoading && !loads.length"
        class="container-max px-4"
      >
        <div class="mx-auto h-16 w-16 text-center">
          <img
            class="loading relative w-full"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading"
          />
        </div>
      </div>
      <div
        v-else-if="isMobile && loads.length && name === 'My Loads'"
        class="container-max flex w-full flex-wrap items-center justify-center px-4 pb-16"
        :class="isMobile ? 'gap-4' : isTablet ? 'gap-6' : 'gap-10'"
      >
        <MyLoadsCardComponent
          v-for="load in loads"
          :key="load.id"
          :load="load"
          @show-assign-vehicle-modal="
            (load, origin, destination) => {
              showAssignVehicleModal = true;
              selectedLoad = load;
              selectedLoadOrigin = origin;
              selectedLoadDestination = destination;
            }
          "
        />
      </div>
      <div
        v-else-if="isMobile && name === 'My Loads'"
        class="container-max flex h-full w-full justify-center px-4"
      >
        <div
          class="flex h-full w-full max-w-[1400px] flex-col items-center justify-center gap-4 self-stretch overflow-hidden rounded-xl bg-white px-4 py-6 outline outline-1 outline-offset-[-1px] outline-neutral-300-a"
        >
          <img
            src="@/assets/trailer-3-wheel.svg"
            class="w-[108px]"
          />
          <div
            class="flex flex-col items-center justify-start gap-4 self-stretch"
          >
            <div
              class="w-3/4 text-center font-medium leading-7 text-steel-900"
              :class="isDesktop ? 'text-lg' : 'text-base'"
            >
              {{
                showAllLoads
                  ? `You don't have any connected loads.`
                  : `You don’t have loads that need your attention right now.`
              }}
            </div>
          </div>
        </div>
      </div>
      <TeleportModalWrapperComponent
        v-if="showFilters"
        margin-top="mt-[60px]"
        :align="'start'"
        :should-teleport="true"
        :is-modal-open="showFilters"
      >
        <MyLoadsDesktopFiltersComponent
          v-if="showFilters"
          :active-tab="activePageTab"
          :is-loading="isLoading"
          @close-modal="showFilters = false"
        />
      </TeleportModalWrapperComponent>
      <TeleportModalWrapperComponent
        margin-top="mt-[60px]"
        :align="'start'"
        :is-modal-open="showAssignVehicleModal"
      >
        <CarrierWorkflowModalComponent
          :load-id="selectedLoad.id"
          :external-load-id="selectedLoad.shipment_identifier"
          :load-ticket-id="selectedLoadTicketId || carrierVisibleTicketId || ''"
          :is-load-trackable="
            selectedLoad.billable_miles
              ? selectedLoad.billable_miles > 150
              : true
          "
          :origin="selectedLoadOrigin"
          :destination="selectedLoadDestination"
          :assigned-vehicle="selectedLoadAssignedVehicle"
          :pending-vehicle="selectedLoadPendingVehicle"
          @close-modal="showAssignVehicleModal = false"
          @open-view-equipment-modal="
            () => {
              showAssignVehicleModal = false;
              showEquipmentModal = true;
            }
          "
        />
      </TeleportModalWrapperComponent>
      <Teleport to="body">
        <displayEquipmentTableModal
          :visible="showEquipmentModal"
          :show-dismiss-button-prop="true"
          @close="
            () => {
              showEquipmentModal = false;
              showAssignVehicleModal = true;
            }
          "
        />
      </Teleport>
    </div>
  </div>
</template>
